import React from "react";

export type StorageGuardrailStatus =
  | "no_data"        // No storage data provided
  | "model_draft"    // Model exists but incomplete
  | "needs_review"   // Has data, needs human review
  | "data_missing";  // Specific data points missing

interface StorageGuardrailBadgeProps {
  status: StorageGuardrailStatus;
  /** Optional: what's missing */
  missingFields?: string[];
  /** Optional: estimated payback years if available */
  paybackYearsEstimate?: number | null;
}

const STATUS_CONFIG: Record<StorageGuardrailStatus, {
  label: string;
  color: string;
  bg: string;
  border: string;
  icon: string;
}> = {
  no_data: {
    label: "Speicher: Keine Daten",
    color: "#374151",
    bg: "#f9fafb",
    border: "#d1d5db",
    icon: "○",
  },
  model_draft: {
    label: "Speicher: Modell in Aufbau",
    color: "#92400e",
    bg: "#fffbeb",
    border: "#fcd34d",
    icon: "◐",
  },
  needs_review: {
    label: "Speicher: Menschliche Prüfung",
    color: "#0c4a6e",
    bg: "#f0f9ff",
    border: "#7dd3fc",
    icon: "◈",
  },
  data_missing: {
    label: "Speicher: Daten unvollständig",
    color: "#7f1d1d",
    bg: "#fef2f2",
    border: "#fca5a5",
    icon: "△",
  },
};

const STORAGE_FIELD_LABELS: Record<string, string> = {
  investmentEur: "Investitionskosten Speicher (€)",
  capacityKwh: "Nutzbare Kapazität (kWh)",
  systemSizeKwp: "PV-Anlagengröße (kWp)",
  annualConsumptionKwh: "Jahresverbrauch (kWh)",
  electricityPriceCentsPerKwh: "Strompreis (Cent/kWh)",
};

export function StorageGuardrailBadge({
  status,
  missingFields,
  paybackYearsEstimate,
}: StorageGuardrailBadgeProps) {
  const cfg = STATUS_CONFIG[status];

  return (
    <div
      style={{
        border: `1px solid ${cfg.border}`,
        borderRadius: 6,
        background: cfg.bg,
        overflow: "hidden",
        fontSize: 12,
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          borderBottom: `1px solid ${cfg.border}`,
        }}
      >
        <span style={{ fontSize: 14, color: cfg.color }}>{cfg.icon}</span>
        <span style={{ fontWeight: 600, color: cfg.color, flex: 1 }}>{cfg.label}</span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: "#6b7280",
            background: "#f3f4f6",
            border: "1px solid #e5e7eb",
            borderRadius: 3,
            padding: "2px 6px",
            whiteSpace: "nowrap",
          }}
        >
          ENTWURF · kein Freigabe-Status
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: "8px 12px" }}>
        {status === "no_data" && (
          <p style={{ margin: 0, color: "#6b7280", lineHeight: 1.5 }}>
            Speicher nicht konfiguriert. Speicher-kWh im Konzept-Formular eingeben um Bewertung zu aktivieren.
          </p>
        )}

        {status === "model_draft" && (
          <p style={{ margin: 0, color: cfg.color, lineHeight: 1.5 }}>
            Speicher konfiguriert. Bewertung noch nicht durchgefuehrt.{" "}
            <strong>&#8594; &bdquo;Projektdaten uebernehmen&ldquo; klicken.</strong>
          </p>
        )}

        {status === "data_missing" && (
          <div>
            {missingFields && missingFields.length > 0 ? (
              <ul style={{ margin: "0 0 6px 0", paddingLeft: 16, color: cfg.color, lineHeight: 1.6 }}>
                {missingFields.map((f) => (
                  <li key={f}>{STORAGE_FIELD_LABELS[f] ?? f}</li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: "0 0 6px 0", color: cfg.color }}>Fehlende Felder nicht spezifiziert.</p>
            )}
            <p style={{ margin: 0, color: "#6b7280", fontStyle: "italic" }}>
              Ohne Investitionskosten keine Amortisationsschaetzung moeglich.
            </p>
          </div>
        )}

        {status === "needs_review" && (
          <p style={{ margin: 0, color: cfg.color, lineHeight: 1.5 }}>
            Datenbasis vollstaendig. Schaetzwert liegt vor.{" "}
            <strong>Menschliche Pruefung erforderlich.</strong>
          </p>
        )}

        {/* Payback row */}
        {paybackYearsEstimate != null && (
          <div
            style={{
              marginTop: 8,
              padding: "6px 10px",
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: 4,
              color: "#14532d",
              fontWeight: 500,
            }}
          >
            Amortisation (Schaetzung): ~{paybackYearsEstimate} Jahre &mdash; Bandbreite &plusmn;30% &mdash; kein Garant.
          </div>
        )}
      </div>

      {/* Footer disclaimer */}
      <div
        style={{
          padding: "6px 12px",
          borderTop: `1px solid ${cfg.border}`,
          background: "#fffbeb",
          color: "#92400e",
          fontSize: 11,
          lineHeight: 1.4,
        }}
      >
        &#9888; Kein Garant fuer Amortisation. Speicher-Wirtschaftlichkeit haengt von Eigenverbrauchsprofil, Energiepreisen und Foerderungen ab. Endbeurteilung durch Fachkraft.
      </div>
    </div>
  );
}

export default StorageGuardrailBadge;
